import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    missionAssigneeName,
    isMissionCaptain
} from '../../utils/group-missions.js';

const MissionsTable = ({
    missions,
    emptyMessage,
    onRowClick,
    onCompleteAction,
    currentUserEmail,
    currentUserTeam,
    isItLeadership,
    hasManagementRights
}) => {
    const [expanded, setExpanded] = useState(() => new Set());

    const toggle = (key) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const missionStatusBadge = (mission) => {
        if (mission.is_complete)
            return (
                <span className="status-badge status-completed">Completed</span>
            );
        if (mission.is_overdue)
            return <span className="status-badge status-overdue">Overdue</span>;
        return <span className="status-badge status-pending">In Progress</span>;
    };

    const assigneeStatusBadge = (task) => {
        if (task.status === 'completed')
            return (
                <span className="status-badge status-completed">Completed</span>
            );
        if (task.sla?.is_overdue)
            return <span className="status-badge status-overdue">Overdue</span>;
        return <span className="status-badge status-pending">Pending</span>;
    };

    const canComplete = (task) =>
        onCompleteAction &&
        task.status !== 'completed' &&
        (isItLeadership ||
            task.assignee_email === currentUserEmail ||
            (hasManagementRights && task.team_id === currentUserTeam));

    return (
        <div className="tasks-table-container mt-4">
            <table className="tasks-table missions-table">
                <thead>
                    <tr>
                        <th style={{ width: '2.5rem' }} />
                        <th>Mission</th>
                        <th>Captain</th>
                        <th>Total Assigned</th>
                        <th>Progress</th>
                        <th>Due Date</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {missions.map((mission) => {
                        const isOpen = expanded.has(mission.key);
                        return (
                            <React.Fragment key={mission.key}>
                                <tr
                                    className="mission-row clickable-row"
                                    onClick={() => toggle(mission.key)}
                                    style={{ cursor: 'pointer' }}>
                                    <td>
                                        <span
                                            className={`drawer-arrow ${
                                                isOpen ? 'open' : ''
                                            }`}>
                                            &#9654;
                                        </span>
                                    </td>
                                    <td>
                                        <div className="task-title">
                                            {mission.title}
                                        </div>
                                    </td>
                                    <td>
                                        {mission.captain_name ? (
                                            <span className="captain-pill">
                                                ★ {mission.captain_name}
                                            </span>
                                        ) : (
                                            <span className="captain-none">
                                                Unassigned
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className="assigned-count">
                                            {mission.total_assigned}
                                        </span>
                                    </td>
                                    <td>
                                        {mission.completed_count}/
                                        {mission.total_assigned}
                                    </td>
                                    <td>{mission.due_date || '-'}</td>
                                    <td>{missionStatusBadge(mission)}</td>
                                </tr>

                                {isOpen &&
                                    mission.assignees.map((task) => (
                                        <tr
                                            key={task.id}
                                            className={`subtask-row ${
                                                onRowClick
                                                    ? 'clickable-row'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                onRowClick && onRowClick(task)
                                            }
                                            style={
                                                onRowClick
                                                    ? { cursor: 'pointer' }
                                                    : {}
                                            }>
                                            <td />
                                            <td>
                                                <div
                                                    className="task-title"
                                                    style={{
                                                        paddingLeft: '1.5rem'
                                                    }}>
                                                    <span className="subtask-indicator">
                                                        ↳{' '}
                                                    </span>
                                                    {missionAssigneeName(task)}
                                                    {isMissionCaptain(
                                                        task,
                                                        mission.captain
                                                    ) && (
                                                        <span className="captain-tag">
                                                            {' '}
                                                            ★ Captain
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="team-pill">
                                                    {task.team_id}
                                                </span>
                                            </td>
                                            <td colSpan={2}>
                                                {assigneeStatusBadge(task)}
                                            </td>
                                            <td>
                                                {task.due_date?.value ||
                                                    task.due_date ||
                                                    '-'}
                                            </td>
                                            <td
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }>
                                                {canComplete(task) && (
                                                    <button
                                                        className="btn-complete"
                                                        onClick={() =>
                                                            onCompleteAction(
                                                                task
                                                            )
                                                        }>
                                                        ✓ Complete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                            </React.Fragment>
                        );
                    })}
                    {missions.length === 0 && (
                        <tr>
                            <td
                                colSpan="7"
                                style={{
                                    textAlign: 'center',
                                    padding: '2rem'
                                }}>
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

MissionsTable.propTypes = {
    missions: PropTypes.array.isRequired,
    emptyMessage: PropTypes.node.isRequired,
    onRowClick: PropTypes.func,
    onCompleteAction: PropTypes.func,
    currentUserEmail: PropTypes.string,
    currentUserTeam: PropTypes.string,
    isItLeadership: PropTypes.bool,
    hasManagementRights: PropTypes.bool
};

export default MissionsTable;
